import { useCallback, useEffect, useState } from 'react';
import { EChartsInstance } from './echartsCore';
import { ChartPlotLine } from '../../../types/ChartPlotLine';

interface PlotLineMarker {
    key: number;
    left: number;
    html: string;
}

interface PlotLineOverlayProps {
    chart: EChartsInstance | null;
    plotLines: ChartPlotLine[];
}

const sameMarkers = (a: PlotLineMarker[], b: PlotLineMarker[]) => {
    return a.length === b.length && a.every((marker, index) => marker.key === b[index].key && marker.left === b[index].left);
};

function PlotLineOverlay(props: PlotLineOverlayProps) {
    const { chart, plotLines } = props;
    const [markers, setMarkers] = useState<PlotLineMarker[]>([]);

    const recompute = useCallback(() => {
        if (!chart || chart.isDisposed() || plotLines.length === 0) {
            setMarkers(current => (current.length === 0 ? current : []));
            return;
        }

        const width = chart.getWidth();
        const next: PlotLineMarker[] = [];

        plotLines.forEach(plotLine => {
            const time = plotLine.timeValue ?? plotLine.value;
            let pixel: number;
            try {
                pixel = chart.convertToPixel({ xAxisIndex: 0 }, time) as number;
            } catch {
                return;
            }
            if (!Number.isFinite(pixel) || pixel < 0 || pixel > width) {
                return;
            }
            next.push({
                key: time,
                left: pixel + (plotLine.label?.x ?? 0),
                html: plotLine.label?.text ?? '',
            });
        });

        setMarkers(current => (sameMarkers(current, next) ? current : next));
    }, [chart, plotLines]);

    useEffect(() => {
        if (!chart || chart.isDisposed()) {
            return;
        }
        chart.on('finished', recompute);
        window.addEventListener('resize', recompute);
        recompute();
        return () => {
            if (!chart.isDisposed()) {
                chart.off('finished', recompute);
            }
            window.removeEventListener('resize', recompute);
        };
    }, [chart, recompute]);

    if (markers.length === 0) {
        return null;
    }

    return (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
            {markers.map(marker => (
                <div
                    key={marker.key}
                    className="chart-plot-line-label highcharts-plot-line-label"
                    style={{ position: 'absolute', left: `${marker.left}px`, pointerEvents: 'auto' }}
                    dangerouslySetInnerHTML={{ __html: marker.html }}
                />
            ))}
        </div>
    );
}

export default PlotLineOverlay;
